<div class="pre-app__panel">
  <div class="step-head">
    <h2 class="step-head__title">Application Summary</h2>
    <p class="step-head__lead">{{ summaryLead() }}</p>
  </div>

  <!-- Live recap of what was shared, fed by the AI doc checks. -->
  <div class="recap" data-testid="summary-recap">
    @for (group of recapGroups(); track group.title) {
      <section class="recap__group" [attr.aria-label]="group.title">
        <h3 class="recap__group-title">{{ group.title }}</h3>
        @for (row of group.rows; track row.docType) {
          <div
            class="recap__row"
            [class.recap__row--ok]="row.tone === 'ok'"
            [class.recap__row--checking]="row.tone === 'checking'"
            [class.recap__row--attention]="row.tone === 'attention'"
            [class.recap__row--missing]="row.tone === 'missing'"
            [attr.data-testid]="'recap-' + row.docType"
          >
            <span class="recap__icon" aria-hidden="true">
              @switch (row.tone) {
                @case ('ok') {
                  <app-icon name="check-circle" [size]="18" />
                }
                @case ('checking') {
                  <app-icon name="clock" [size]="18" />
                }
                @case ('attention') {
                  <app-icon name="alert-triangle" [size]="18" />
                }
                @default {
                  <app-icon name="plus" [size]="18" />
                }
              }
            </span>
            <div class="recap__body">
              <span class="recap__label">{{ row.label }}</span>
              <span class="recap__text">{{ row.text }}</span>
              @if (row.files.length > 0) {
                <ul class="recap__files">
                  @for (f of row.files; track $index) {
                    <li>
                      <app-icon name="file-text" [size]="13" />
                      <span>{{ f }}</span>
                    </li>
                  }
                </ul>
              }
            </div>
            @if (row.actionLabel) {
              <button
                type="button"
                class="recap__action"
                (click)="jumpTo.emit(row.step)"
                [attr.data-testid]="'recap-action-' + row.docType"
              >
                {{ row.actionLabel }}
              </button>
            }
          </div>
        }
      </section>
    }
  </div>

  <!-- What happens next — adapts to the DF unlock state. -->
  <section class="next-steps" aria-label="What happens next">
    <h3 class="next-steps__title">What Happens Next</h3>
    <ul class="next-steps__list">
      <li>
        <app-icon name="clock" [size]="16" />
        <span>Our team reviews your application within one business day.</span>
      </li>
      <li data-testid="next-steps-products">
        <app-icon name="zap" [size]="16" />
        <span>
          @if (dfUnlocked()) {
            You'll receive your funding options for both Kickstart Cash and Debtors Finance.
          } @else {
            You'll receive your Kickstart Cash funding options.
          }
        </span>
      </li>
      <li>
        <app-icon name="shield" [size]="16" />
        <span>No hidden fees or surprise charges.</span>
      </li>
    </ul>
  </section>

  <!-- Debtors Finance unlock — keys off the client's own uploads (N4). -->
  <section
    class="df-unlock"
    [class.df-unlock--available]="dfUnlocked()"
    aria-label="Debtors Finance"
    data-testid="df-unlock"
  >
    <div class="df-unlock__head">
      <span class="df-unlock__icon" aria-hidden="true">
        <app-icon [name]="dfUnlocked() ? 'zap' : 'lock'" [size]="20" />
      </span>
      <div class="df-unlock__titles">
        <h3 class="df-unlock__title">Unlock a higher facility with Debtors Finance</h3>
        <p class="df-unlock__status">
          <span class="df-unlock__status-label">Status:</span>
          @if (dfUnlocked()) {
            <strong>Available</strong>
          } @else {
            <strong>Locked</strong>
          }
        </p>
      </div>
    </div>
    <p class="df-unlock__body">
      @if (dfUnlocked()) {
        Based on your debtors age analysis, you may qualify for a higher facility through Debtors
        Finance — cash advanced against your unpaid invoices.
      } @else {
        Debtors Finance advances cash against your unpaid invoices. Share your debtors age analysis
        to be considered for a higher facility.
      }
    </p>
    @if (dfUnlocked()) {
      <p class="df-unlock__fine">Your actual facility is confirmed after review.</p>
    } @else {
      <button
        mat-stroked-button
        type="button"
        (click)="jumpTo.emit('financials')"
        data-testid="df-unlock-add"
      >
        <app-icon name="plus" [size]="18" />
        <span>Add debtor information</span>
      </button>
    }
  </section>

  <!--
    The no-financials review's tip (master document). Worded "now" and
    pointing at the Add actions above: a SUBMITTED enquiry reopens only
    when Ops asks, so nothing here promises "later". No amount, no size
    element (UI-DA-58-01).
  -->
  @if (!hasFinancialDocs()) {
    <p class="pre-app__tip" data-testid="offers-financials-tip">
      <app-icon name="info" [size]="16" />
      <span>
        Your bank data is enough to submit. Adding financial information now — or later, from your
        dashboard, while CapX is reviewing — may unlock a higher Kickstart Cash amount and Debtors
        Finance for a larger facility. Use the Add buttons above.
      </span>
    </p>
  }

  @if (bankStatementCount() === 0) {
    <p class="pre-app__encourage" data-testid="offers-bank-nudge">
      <app-icon name="info" [size]="16" />
      <span>
        Bank statements give us the most to work with. Adding a few months in Step 1 speeds up your
        decision.
      </span>
    </p>
  }

  <div class="pre-app__nav">
    <button mat-stroked-button type="button" (click)="back.emit()" data-testid="step-offers-back">
      <app-icon name="arrow-left" [size]="18" />
      <span>Back</span>
    </button>
    <div class="pre-app__actions">
      @if (dfUnlocked()) {
        <button
          mat-stroked-button
          type="button"
          [disabled]="!canSubmit() || isSubmitting()"
          (click)="submitTrack.emit(false)"
          data-testid="preapp-submit"
        >
          @if (submittingTrack() === 'ks') {
            <mat-spinner diameter="20" />
          }
          <span>Submit for Kickstart Cash only</span>
        </button>
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="!canSubmit() || isSubmitting()"
          (click)="submitTrack.emit(true)"
          data-testid="preapp-submit-df"
        >
          @if (submittingTrack() === 'df') {
            <mat-spinner diameter="20" />
          }
          <span>Submit &amp; unlock Debtors Finance</span>
        </button>
      } @else {
        <button
          mat-flat-button
          color="primary"
          type="button"
          [disabled]="!canSubmit() || isSubmitting()"
          (click)="submitTrack.emit(false)"
          data-testid="preapp-submit"
        >
          @if (isSubmitting()) {
            <mat-spinner diameter="20" />
          }
          <span>{{ reopened() ? 'Submit again' : 'Submit for a decision' }}</span>
        </button>
      }
    </div>
  </div>
  @if (anyInFlight()) {
    <p class="pre-app__submit-hint">Waiting for your documents to finish uploading…</p>
  }
  <!--
    With Debtors Finance unlocked there are two submit tracks, and the
    master's "processed in parallel" is reworded: this is ONE enquiry
    with an interest flag, reviewed together, not two live applications.
    The master's "add Debtors Finance later from your dashboard" is
    dropped — no such path exists (ticket, Gap 3 decision (b)).
  -->
  @if (dfUnlocked()) {
    <p class="pre-app__footnote" data-testid="offers-reviewed-together">
      Both are reviewed together. You'll see your options when CapX comes back to you.
    </p>
  }
</div>
